import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import type { OpenAPI } from "simplydo/interfaces";
import { CheckboxHeader } from "simplydo/react/components/CheckboxHeader";
import { Button, Divider, Form, Icon, Message, Popup } from "simplydo/react/ui";
import { Input } from "simplydo/react/ui";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import type {
  RoleForType,
  RoleSettingsUpdateBody,
  RoleWithInformation,
  RolesUser,
} from "../types";
import RoleEditorIconChooser from "./IconChooser";

type RoleEditorSettingsProps = {
  forType: OpenAPI.Schemas["Role"]["forType"];
  setRoles: Dispatch<SetStateAction<RoleWithInformation[]>>;
  canSetRolesAsDefault: boolean;
  roleId: string;
  usingRole: RoleWithInformation | undefined;
  forId: string;
  roles: RoleWithInformation[];
};

const RoleEditorSettings = ({
  forType,
  setRoles,
  canSetRolesAsDefault,
  roleId,
  usingRole,
  forId,
  roles,
}: RoleEditorSettingsProps) => {
  const [updatedRoleName, setUpdatedRoleName] = useState<string>("");
  const [_settingDefaultRole, setSettingDefaultRole] = useState<string>("");
  const [defaultRoleToggleActive, setDefaultRoleToggleActive] =
    useState<boolean>(!!usingRole?.isDefaultRole);
  const user: RolesUser = useAppSelector((state) => state.user);

  useEffect(() => {
    setUpdatedRoleName(usingRole?.name ?? "");
  }, [usingRole?.name]);

  const { t } = useTranslation();
  const typeNames: Record<RoleForType, string> = useMemo(
    () => ({
      organisation: "organisation",
      group: "group",
      global: "global",
      ideaBusinessProfile: "business profile",
      challenge: t("generic.challenge"),
      discussion: "discussion",
      innointelList: "innointel list",
    }),
    [t],
  );

  const updateRole = useCallback(
    (data: RoleSettingsUpdateBody) => {
      api.roles.updateRole(
        forType,
        forId,
        roleId,
        data,
        ({ role }) => {
          toast.success("Role updated");
          setRoles((prevRoles) => {
            const newRoles = [...prevRoles];
            const index = newRoles.findIndex((role) => role._id === roleId);
            if (index >= 0) {
              newRoles[index] = {
                ...newRoles[index],
                ...role,
              } as RoleWithInformation;
            }
            return newRoles;
          });
        },
        (err) => {
          toast.error(err.message);
        },
      );
    },
    [forType, roleId, forId, setRoles],
  );

  const updateRoleIcon = useCallback(
    (data: Partial<OpenAPI.Schemas["Role"]>) => {
      updateRole(data as RoleSettingsUpdateBody);
    },
    [updateRole],
  );

  const roleWarningPlaceholder = useMemo(() => {
    if (forType === "organisation") {
      return `users belonging to ${user?.ownerOrganisation?.name ?? "the organisation"}`;
    }
    if (forType === "challenge") {
      return `audience members of the ${t("generic.challenge")}`;
    }
    return `members of the ${typeNames[forType as RoleForType]}`;
  }, [forType, typeNames, t, user?.ownerOrganisation?.name]);

  const setRoleAsDefault = useCallback(
    (selectedRoleId: string) => {
      const selectedRole = roles.find((role) => role._id === selectedRoleId);
      if (!selectedRole) return;

      util
        .confirm(
          selectedRole.isDefaultRole ?
            "Revoke default role"
          : "Make role default",
          selectedRole.isDefaultRole ?
            `Removing "${selectedRole.name}" as a default role from this ${typeNames[forType as RoleForType]} will immediately remove all associated permissions from the relevant users.`
          : `Default roles will automatically apply to all current and future ${roleWarningPlaceholder}. This will grant them all permissions of the "${selectedRole.name}" role for as long as it remains default. Please make sure you check the permissions carefully before proceeding.`,
        )
        .then(() => {
          setDefaultRoleToggleActive(!defaultRoleToggleActive);
          const apiFunc =
            selectedRole.isDefaultRole ?
              api.roles.removeDefaultRole
            : api.roles.addDefaultRole;
          setSettingDefaultRole(selectedRoleId);
          apiFunc(
            forType,
            forId,
            selectedRoleId,
            () => {
              setSettingDefaultRole("");
              setRoles((prevRoles) =>
                prevRoles.map((role) => {
                  if (role._id === selectedRoleId) {
                    return { ...role, isDefaultRole: !role.isDefaultRole };
                  }
                  return role;
                }),
              );
            },
            (err) => {
              toast.error(err.message);
              setSettingDefaultRole("");
            },
          );
        })
        .catch(() => {});
    },
    [
      roles,
      forType,
      forId,
      setRoles,
      typeNames,
      defaultRoleToggleActive,
      roleWarningPlaceholder,
    ],
  );

  const saveRoleName = useCallback(() => {
    updateRole({ name: updatedRoleName });
  }, [updateRole, updatedRoleName]);

  if (!usingRole) {
    return null;
  }

  return (
    <>
      <Form>
        <Form.Field
          control={Input}
          label="Role name"
          placeholder="Role name"
          value={updatedRoleName ?? usingRole.name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setUpdatedRoleName(e.target.value)
          }
          fluid
          action={
            <Button primary content="Save" onClick={() => saveRoleName()} />
          }
        />
      </Form>
      <Divider hidden />
      <span>
        <b>Icon</b>
      </span>
      <span>
        Role icons are displayed next to users names across {util.appName()}, if
        they have this role assigned to them.
      </span>
      <RoleEditorIconChooser
        containerStyle={{ margin: "10px 0 0" }}
        role={usingRole}
        updateRole={updateRoleIcon}
      />

      <Divider hidden />
      {canSetRolesAsDefault && !usingRole.preventUserEditing ?
        <>
          <Popup
            hoverable
            content="This role is assigned to every user by default."
            trigger={
              <CheckboxHeader
                header="Set as a default role"
                description={`Assign this role and all its permissions to all users in this ${typeNames[forType as RoleForType]}.`}
                as="h5"
                checked={defaultRoleToggleActive}
                onChange={() => {
                  setRoleAsDefault(roleId);
                }}
              />
            }
          />
          {usingRole.isDefaultRole ?
            <Message warning>
              <Icon name="warning sign" />
              This role is now being applied to all{" "}
              {forType === "organisation" ?
                `users belonging to ${user?.ownerOrganisation?.name ?? "the organisation"}. All users will receive these permissions.`
              : forType === "challenge" ?
                `users in the audience of the ${t("generic.challenge")}. All audience members will receive these permissions.`
              : `members of the ${typeNames[forType as RoleForType]}. All members will receive these permissions.`
              }
            </Message>
          : null}
        </>
      : null}
    </>
  );
};

export default RoleEditorSettings;
